import {THREE} from '../../base/three-defs.js';

import * as entity from "../../base/entity.js";
import * as shaders from "./shaders.js";
import {generateCloudNoiseTexture} from './cloud-texture-generator.js';
import {AtmosphereLUTs, SunTransmittanceCPU} from './atmosphere-luts.js';
import {NIGHT_SKY_LIGHT} from '../../base/threejs-component.js';
import {IS_MOBILE} from '../../base/quality.js';

// Observer latitude for the sun's arc (declination 0, an equinox day):
// noon elevation ~55 degrees, proper low golden-hour sun.
const SUN_LATITUDE = 35 * Math.PI / 180;

// Default clock: just before sunrise, so a fresh load shows the atmosphere
// doing its best work within a minute of dragging the slider.
const DEFAULT_TIME_OF_DAY = 6.75;

const ATMO_GROUND_RADIUS_KM = 6360.0;

// Daylight land-lighting targets. The directional light takes its colour
// from the CPU sun transmittance (so it reddens exactly as the shader's
// sun does); these set its strength and the ambient's hue. Step 4
// (physical exposure) will derive the ambient from the sky-view LUT
// instead.
const DAY_LIGHT_INTENSITY = 3.2;
const DAY_HEMI_SKY = new THREE.Color(0.42, 0.62, 0.95);
const DAY_HEMI_GROUND = new THREE.Color(0.34, 0.42, 0.25);
const DAY_HEMI_INTENSITY = 0.9;

function smoothstep(a, b, x) {
  const t = Math.min(Math.max((x - a) / (b - a), 0), 1);
  return t * t * (3 - 2 * t);
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

export class RenderSkyComponent extends entity.Component {
  static CLASS_NAME = 'RenderSkyComponent';

  get NAME() {
    return RenderSkyComponent.CLASS_NAME;
  }

  constructor() {
    super();
  }

  InitEntity() {
    const threejs = this.FindEntity('threejs').GetComponent('ThreeJSController');

    // 1x1x1 placeholder so the sampler is valid until generation finishes.
    const placeholder = new THREE.Data3DTexture(
        new Uint8Array([0, 0, 0, 255]), 1, 1, 1);
    placeholder.format = THREE.RGBAFormat;
    placeholder.needsUpdate = true;

    const textureLoader = new THREE.TextureLoader();
    const blueNoise = textureLoader.load('./textures/bluenoise.png');
    blueNoise.wrapS = THREE.RepeatWrapping;
    blueNoise.wrapT = THREE.RepeatWrapping;
    blueNoise.minFilter = THREE.NearestFilter;
    blueNoise.magFilter = THREE.NearestFilter;

    // NASA LROC albedo map (svs.gsfc.nasa.gov/4720), equirectangular.
    const moonAlbedo = textureLoader.load('./textures/moon.jpg');
    moonAlbedo.colorSpace = THREE.SRGBColorSpace;
    moonAlbedo.minFilter = THREE.LinearMipMapLinearFilter;
    moonAlbedo.magFilter = THREE.LinearFilter;
    moonAlbedo.wrapS = THREE.RepeatWrapping;
    moonAlbedo.wrapT = THREE.ClampToEdgeWrapping;

    // Shared between the raymarch material and the display dome so a
    // single write updates both (same records, not copies).
    const lutPlaceholder = new THREE.DataTexture(
        new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
    lutPlaceholder.needsUpdate = true;
    this.atmoUniforms_ = {
      uAtmoTransmittanceLUT: { value: lutPlaceholder },
      uAtmoMultiScatterLUT: { value: lutPlaceholder },
      uAtmoSkyViewLUT: { value: lutPlaceholder },
      uAtmoSunDir: { value: new THREE.Vector3(0, -1, 0) },
      uAtmoViewHeight: { value: ATMO_GROUND_RADIUS_KM + 0.002 },
      uAtmoDayFactor: { value: 0.0 },
      // Moon direction (rides the sun's arc half a day out of phase) and
      // the dominant shadow/sheen light (sun by day, moon by night).
      uMoonDir: { value: new THREE.Vector3(-1, 0.45, 1).normalize() },
      uShadowLightDir: { value: new THREE.Vector3(-1, 0.45, 1).normalize() },
    };

    const uniforms = {
      "time": { value: 0.0 },
      "frame": { value: 0.0 },
      "perlinWorley": { value: placeholder },
      "cloudsReady": { value: 0.0 },
      "blueNoise": { value: blueNoise },
      ...this.atmoUniforms_,
    };

    // Outside the far terrain shell's corners (~17km), inside the camera
    // far plane - the shell must depth-test in front of the sky. The
    // dense tessellation keeps per-fragment view directions accurate at
    // the frame corners (coarse domes showed radial cloud distortion
    // there in the blind audit).
    const skyGeo = new THREE.SphereGeometry(25000, 64, 32);
    const skyMat = new shaders.ShaderMaterial('SKY', {
        uniforms: uniforms,
        defines: {
          // Half-res rendering pays for more steps than before (40/24).
          // Trimmed from 64/32 when the de-tiled coverage added a second
          // fetch per step; visually indistinguishable.
          CLOUD_STEPS: IS_MOBILE ? '28' : '56',
          CLOUD_LIGHT_STEPS: IS_MOBILE ? '4' : '6',
          CLOUD_DETAIL_MAX_DIST: IS_MOBILE ? '1500.0' : '4000.0',
        },
        side: THREE.BackSide
    });

    // The sky + clouds are raymarched at half resolution in their own pass...
    this.sky_ = new THREE.Mesh(skyGeo, skyMat);
    this.sky_.castShadow = false;
    this.sky_.receiveShadow = false;

    threejs.AddSceneObject(this.sky_, {pass: 'clouds'});

    // ...temporally accumulated (see CloudAccumulateShader), and a display
    // dome in the main pass samples the accumulated buffer, getting
    // depth-tested against the terrain and linearly upsampled.
    const displayMat = new shaders.ShaderMaterial('SKY-DISPLAY', {
        uniforms: {
          cloudTexture: { value: threejs.CloudTexture },
          resolution: { value: new THREE.Vector2(1, 1) },
          time: { value: 0.0 },
          moonAlbedo: { value: moonAlbedo },
          ...this.atmoUniforms_,
        },
        side: THREE.BackSide
    });

    this.skyDisplay_ = new THREE.Mesh(skyGeo, displayMat);
    this.skyDisplay_.castShadow = false;
    this.skyDisplay_.receiveShadow = false;

    threejs.AddSceneObject(this.skyDisplay_);

    // Shared with the grass/terrain shaders so cloud shadows on the land
    // are computed from the exact field the sky raymarcher renders.
    threejs.cloudShadow_ = { texture: null, time: 0 };

    generateCloudNoiseTexture(threejs.Renderer).then((texture) => {
      skyMat.uniforms.perlinWorley.value = texture;
      skyMat.uniforms.cloudsReady.value = 1.0;
      threejs.cloudShadow_.texture = texture;
    });

    this.atmosphere_ = new AtmosphereLUTs();
    this.atmosphere_.init(threejs.Renderer).then(() => {
      this.atmoUniforms_.uAtmoTransmittanceLUT.value =
          this.atmosphere_.transmittanceTexture;
      this.atmoUniforms_.uAtmoMultiScatterLUT.value =
          this.atmosphere_.multiScatterTexture;
      this.atmoUniforms_.uAtmoSkyViewLUT.value =
          this.atmosphere_.skyViewTexture;
    }).catch((e) => {
      console.error('Atmosphere LUT init failed:', e);
    });

    this.timeOfDay_ = DEFAULT_TIME_OF_DAY;
    this.#CreateTimeSlider_();

    // Debug handle while the day-cycle work is in flight (steps 2-4).
    window.__skyComponent = this;

    // Scratch objects for the per-frame light blend (no allocation). The
    // light direction IS the uShadowLightDir uniform's vector, mutated in
    // place so shaders and the light rig always agree.
    this.sunTransTmp_ = [0, 0, 0];
    this.lightDirTmp_ = this.atmoUniforms_.uShadowLightDir.value;
    this.lightColourTmp_ = new THREE.Color();
    this.dayColourTmp_ = new THREE.Color();
    this.hemiSkyTmp_ = new THREE.Color();
    this.hemiGroundTmp_ = new THREE.Color();
    this.skyLightParams_ = {
      direction: this.lightDirTmp_,
      colour: this.lightColourTmp_,
      intensity: 1.0,
      hemiSky: this.hemiSkyTmp_,
      hemiGround: this.hemiGroundTmp_,
      hemiIntensity: NIGHT_SKY_LIGHT.hemiIntensity,
    };
  }

  // Debug/exploration control until the cycle runs on its own clock.
  #CreateTimeSlider_() {
    const existing = document.getElementById('time-of-day-control');
    if (existing) {
      existing.remove();
    }

    const wrap = document.createElement('div');
    wrap.id = 'time-of-day-control';
    wrap.style.cssText =
        'position:fixed;bottom:16px;left:50%;transform:translateX(-50%);' +
        'display:flex;align-items:center;gap:10px;z-index:20;' +
        'font:12px monospace;color:#cfd8e3;opacity:0.85;user-select:none;';

    const label = document.createElement('span');
    label.style.cssText = 'min-width:5ch;text-align:right;';

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '24';
    slider.step = '0.01';
    slider.value = String(this.timeOfDay_);
    slider.style.cssText = 'width:min(40vw,320px);accent-color:#8fa8c8;';

    const setLabel = () => {
      const h = Math.floor(this.timeOfDay_);
      const m = Math.floor((this.timeOfDay_ - h) * 60);
      label.textContent =
          String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
    };
    setLabel();

    slider.addEventListener('input', () => {
      this.timeOfDay_ = parseFloat(slider.value);
      setLabel();
    });
    // Return key focus to the game so WASD does not scrub the slider.
    slider.addEventListener('change', () => slider.blur());

    wrap.appendChild(label);
    wrap.appendChild(slider);
    document.body.appendChild(wrap);
  }

  // Sun direction for an hour-of-day: equinox arc at SUN_LATITUDE, with
  // x = east and z pointing away from the noon sun. The vector is unit by
  // construction (cos^2 lat cos^2 HA + sin^2 HA + sin^2 lat cos^2 HA = 1).
  #SunDirection_(t, out) {
    const ha = (t - 12) / 12 * Math.PI;
    out.set(
        -Math.sin(ha),
        Math.cos(SUN_LATITUDE) * Math.cos(ha),
        -Math.sin(SUN_LATITUDE) * Math.cos(ha));
    return out;
  }

  Update(timeElapsed) {
    const player = this.FindEntity('player');
    if (!player) {
      return;
    }
    const pos = player.Position;

    if (this.sky_) {
      this.sky_.material.uniforms.time.value += timeElapsed;
      this.sky_.material.uniforms.frame.value =
          (this.sky_.material.uniforms.frame.value + 1.0) % 1024.0;
      this.sky_.position.copy(pos);

      const threejs = this.FindEntity('threejs').GetComponent('ThreeJSController');
      if (threejs.cloudShadow_) {
        threejs.cloudShadow_.time = this.sky_.material.uniforms.time.value;
      }
    }

    if (this.skyDisplay_) {
      const threejs = this.FindEntity('threejs').GetComponent('ThreeJSController');
      const canvas = threejs.Renderer.domElement;
      this.skyDisplay_.material.uniforms.cloudTexture.value = threejs.CloudTexture;
      this.skyDisplay_.material.uniforms.resolution.value.set(canvas.width, canvas.height);
      this.skyDisplay_.material.uniforms.time.value += timeElapsed;
      this.skyDisplay_.position.copy(pos);
    }

    if (this.atmosphere_ && this.atmosphere_.ready) {
      const threejs = this.FindEntity('threejs').GetComponent('ThreeJSController');
      const sunDir = this.#SunDirection_(
          this.timeOfDay_, this.atmoUniforms_.uAtmoSunDir.value);
      const sinElev = sunDir.y;

      // 1 unit = 1 m; keep the camera strictly above the model's ground so
      // the horizon sqrt stays real.
      const viewHeightKm =
          ATMO_GROUND_RADIUS_KM + Math.max(pos.y, 1.0) / 1000.0;
      this.atmoUniforms_.uAtmoViewHeight.value = viewHeightKm;

      // Atmosphere takes over through nautical/civil twilight; below -10
      // degrees the hand-tuned night pipeline is untouched. Star/moon
      // visibility is not keyed here - the display shader derives it from
      // sky luminance (contrast washout).
      this.atmoUniforms_.uAtmoDayFactor.value =
          smoothstep(-0.18, -0.02, sinElev);

      this.atmosphere_.renderSkyView(threejs.Renderer, sinElev, viewHeightKm);

      const day = this.atmoUniforms_.uAtmoDayFactor.value;

      // Shared state for the land materials (terrain/grass components
      // copy these into their uniforms each frame, like cloudShadow_).
      // The moon rides the same arc half a day out of phase, offset half
      // an hour past exact opposition (an exactly antipodal moon reads as
      // an eclipse diagram; this keeps it near-full at night without the
      // geometry looking staged).
      const moonDir = this.#SunDirection_(
          this.timeOfDay_ + 12.5, this.atmoUniforms_.uMoonDir.value);

      if (!threejs.atmosphereState_) {
        threejs.atmosphereState_ = {
          skyViewTexture: null,
          sunDir: new THREE.Vector3(),
          dayFactor: 0,
          viewHeightKm: viewHeightKm,
          // Live reference to this component's CPU sun transmittance
          // ([r,g,b], updated in place below).
          sunTransmittance: this.sunTransTmp_,
          // Live references, mutated in place each frame.
          moonDir: this.atmoUniforms_.uMoonDir.value,
          lightDir: this.atmoUniforms_.uShadowLightDir.value,
        };
      }
      const st = threejs.atmosphereState_;
      st.skyViewTexture = this.atmosphere_.skyViewTexture;
      st.sunDir.copy(sunDir);
      st.dayFactor = day;
      st.viewHeightKm = viewHeightKm;

      // Land lighting: blend the moonlit night rig toward the sun. The
      // sun's colour is its transmittance (computed with the same integral
      // the LUT uses), so land light reddens exactly as the sky's sun
      // does, and its intensity follows the transmitted luminance -
      // golden-hour light dims and warms with no keyframes.
      const trans = SunTransmittanceCPU(sinElev, this.sunTransTmp_);
      const transLum =
          0.2126 * trans[0] + 0.7152 * trans[1] + 0.0722 * trans[2];

      // The directional rig hands over to the sun only as fast as the sun
      // actually delivers light, not on the clock: blending by dayFactor
      // alone dragged the light colour toward the sun's near-black
      // pre-sunrise transmittance while the moon was still the real
      // source, making 05:30 darker than midnight (the audit's
      // "non-monotonic day-night curve").
      const sunStrength = day * Math.min(transLum * 4.0, 1.0);

      const night = NIGHT_SKY_LIGHT;
      this.lightDirTmp_.copy(moonDir)
          .multiplyScalar(1.0 - sunStrength)
          .addScaledVector(sunDir, sunStrength)
          .normalize();
      // Chromatic adaptation: the eye white-balances a high sun to
      // white, so the meadow must stay green at midday instead of going
      // olive under warm-tinted sunlight. Whiten the transmittance as
      // the sun climbs; the low golden-hour sun keeps its full colour.
      const whiten = smoothstep(0.25, 0.7, sinElev) * 0.55;
      this.dayColourTmp_.setRGB(
          lerp(trans[0], transLum, whiten),
          lerp(trans[1], transLum, whiten),
          lerp(trans[2], transLum, whiten));
      this.lightColourTmp_.copy(night.colour)
          .lerp(this.dayColourTmp_, sunStrength);
      this.hemiSkyTmp_.copy(night.hemiSky).lerp(DAY_HEMI_SKY, day);
      this.hemiGroundTmp_.copy(night.hemiGround).lerp(DAY_HEMI_GROUND, day);

      // The transmittance colour already carries the sun's dimming and
      // reddening (it IS the attenuation), so intensity stays constant -
      // scaling by luminance too would dim golden hour quadratically.
      const p = this.skyLightParams_;
      p.intensity = lerp(night.intensity, DAY_LIGHT_INTENSITY, sunStrength);
      p.hemiIntensity = lerp(
          night.hemiIntensity,
          DAY_HEMI_INTENSITY * lerp(0.35, 1.0, transLum), day);
      threejs.SetSkyLight(p);
    }
  }
}
